import React, { Component } from "react";
import { Form, Button } from "react-bootstrap";

/**
 * DashboardAIChatbot Component
 * Floating AI Assistant analyzing real-time Dashboard state.
 */
class DashboardAIChatbot extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isOpen: false,
            isMinimized: false,
            isMaximized: false,
            inputMessage: "",
            messages: [
                {
                    sender: "bot",
                    text: "Hello! I am your AI Dashboard Assistant. Ask me anything about your assigned classes, section attendance, subject performance, top students, doubts, or accessible modules!",
                    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                },
            ],
        };
        this.chatEndRef = React.createRef();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.messages.length !== this.state.messages.length) {
            this.scrollToBottom();
        }
    }

    scrollToBottom = () => {
        if (this.chatEndRef.current) {
            this.chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    toggleChat = () => {
        this.setState((prev) => ({
            isOpen: !prev.isOpen,
            isMinimized: false,
        }));
    };

    toggleMinimize = (e) => {
        e.stopPropagation();
        this.setState((prev) => ({
            isMinimized: !prev.isMinimized,
        }));
    };

    toggleMaximize = (e) => {
        e.stopPropagation();
        this.setState((prev) => ({
            isMaximized: !prev.isMaximized,
            isMinimized: false,
        }));
    };

    handleQuestionClick = (questionText) => {
        this.processUserMessage(questionText);
    };

    handleSendMessage = (e) => {
        e.preventDefault();
        const { inputMessage } = this.state;
        if (!inputMessage.trim()) return;
        const msg = inputMessage.trim();
        this.setState({ inputMessage: "" });
        this.processUserMessage(msg);
    };

    processUserMessage = (userQuery) => {
        const timeNow = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const userMsgObj = { sender: "user", text: userQuery, time: timeNow };

        this.setState(
            (prev) => ({
                messages: [...prev.messages, userMsgObj],
            }),
            () => {
                setTimeout(() => {
                    const botResponse = this.generateAIResponse(userQuery);
                    const botMsgObj = { sender: "bot", text: botResponse, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) };
                    this.setState((prev) => ({
                        messages: [...prev.messages, botMsgObj],
                    }));
                }, 300);
            }
        );
    };

    generateAIResponse = (query) => {
        const qRaw = query.toLowerCase();
        // Replace hyphens with spaces to match both "class-wise" and "class wise"
        const q = qRaw.replace(/-/g, " ");

        const {
            classOptions = [],
            allSectionOptions = [],
            totalStudents = 0,
            presentCount = 0,
            absentCount = 0,
            averageAttendance = 0,
            subjectPerformance = [],
            classPerformance = [],
            sectionPerformance = [],
            topStudents = [],
            attentionStudents = [],
            studentDoubts = [],
            examReports = [],
            lecturerName = "Lecturer",
            modules = [],
        } = this.props;

        const getSectionLabel = (s) => {
            if (!s) return "";
            const secName = s.section_name || s.sectionName || s.section;
            const clsName = s.class_name || s.className;
            if (secName && clsName) return `${clsName} - ${secName}`;
            if (secName) return secName;
            if (clsName) return `Class ${clsName}`;
            if (allSectionOptions.length > 0) return allSectionOptions[0].label;
            return "";
        };

        // 1. Class-wise & Section-wise Attendance & Details
        if (
            q.includes("class wise") ||
            q.includes("section wise") ||
            q.includes("class performance") ||
            q.includes("section performance") ||
            (q.includes("attendance") && (q.includes("class") || q.includes("section") || q.includes("detail")))
        ) {
            let res = "📊 **Class & Section-wise Performance & Attendance Details**:\n\n";

            if (classPerformance.length > 0) {
                res += "🏫 **Class-wise Attendance & Turnout**:\n";
                classPerformance.forEach((c) => {
                    res += `• **${c.className || c.name || "Class"}**: ${c.percentage || c.pct || averageAttendance}% (${c.presentCount || 0} Present / ${c.totalStudents || 0} Total)\n`;
                });
                res += "\n";
            }

            if (sectionPerformance.length > 0) {
                res += "📌 **Section-wise Attendance Breakdown**:\n";
                sectionPerformance.forEach((s) => {
                    res += `• **${s.sectionName || s.name || "Section"}** (${s.className || ""}): ${s.percentage || s.pct || 80}% Turnout\n`;
                });
            } else {
                const secListStr = allSectionOptions.map((sec) => sec.label).join(", ") || "All Assigned Sections";
                res += `📌 **Assigned Scope**: ${secListStr}\n• **Total Enrolled**: **${totalStudents} Students** across **${classOptions.length} Classes** and **${allSectionOptions.length} Sections**.\n• **Average Attendance**: **${averageAttendance}%** (${presentCount} Present / ${absentCount} Absent today).`;
            }
            return res;
        }

        // 2. Attendance General (Today's, Turnout)
        if (q.includes("attendance")) {
            return `📅 **Today's Attendance Summary**:\n\n• **Average Turnout**: **${averageAttendance}%**\n• **Present Students**: **${presentCount}**\n• **Absent Students**: **${absentCount}**\n• **Assigned Classes**: **${classOptions.length}**\n• **Assigned Sections**: **${allSectionOptions.length}**`;
        }

        // 3. Subject Performance
        if (q.includes("subject")) {
            if (subjectPerformance.length === 0) {
                return `📚 **Subject Performance**:
Subject average statistics are being updated across assigned classes. Average overall class performance is **${averageAttendance}%**.`;
            }
            let res = "📚 **Subject-wise Performance & Averages**:\n\n";
            subjectPerformance.forEach((sub) => {
                res += `• **${sub.subjectName || sub.subject}**: Avg **${sub.avgPct || sub.pct || 75}%** (Highest: **${sub.highPct || 90}%**)\n`;
            });
            return res;
        }

        // 4. Top Performing Students
        if (q.includes("top performing") || q.includes("top student") || q.includes("top performer") || q.includes("top rank") || q.includes("rank 1")) {
            if (topStudents.length === 0) {
                return `🏆 **Top Performing Students**: Top rankers are being calculated from recent exam reports.`;
            }
            let res = "🏆 **Top Performing Students**:\n\n";
            topStudents.slice(0, 5).forEach((s, idx) => {
                const secLabel = getSectionLabel(s);
                const secStr = secLabel ? ` (${secLabel})` : "";
                res += `• **#${idx + 1} ${s.student_name || s.name || "Student"}**: **${s.percentage || s.pct || 90}%**${secStr}\n`;
            });
            return res;
        }

        // 5. Students Needing Attention
        if (q.includes("attention") || q.includes("low attendance") || q.includes("absent today") || q.includes("at risk") || q.includes("support")) {
            let res = `⚠️ **Students Needing Attention & Academic Support**:\n\n`;
            res += `• **Absent Today**: **${absentCount} students** absent\n`;
            if (attentionStudents.length > 0) {
                res += `• **Low Turnout / Performance List (${attentionStudents.length})**:\n`;
                attentionStudents.slice(0, 5).forEach((s) => {
                    const secLabel = getSectionLabel(s);
                    const secStr = secLabel ? ` (${secLabel})` : "";
                    res += `  - **${s.student_name || s.name || "Student"}**${secStr}: Attendance/Score **${s.percentage || s.pct || "<75%"}**\n`;
                });
            } else {
                res += `• Overall attendance rate is **${averageAttendance}%**.`;
            }
            return res;
        }

        // 6. Student Doubts & Clarifications (Latest, Solved, Pending)
        if (q.includes("doubt") || q.includes("question") || q.includes("pending") || q.includes("solved") || q.includes("clarification")) {
            const pendingList = studentDoubts.filter((d) => (d.status || d.doubt_status || "").toLowerCase().includes("pend") || !d.is_resolved);
            const solvedList = studentDoubts.filter((d) => (d.status || d.doubt_status || "").toLowerCase().includes("clarifi") || (d.status || "").toLowerCase().includes("resolv") || d.is_resolved);

            let res = `❓ **Student Doubts & Clarifications Overview**:\n\n`;
            res += `• **Total Doubts Raised**: **${studentDoubts.length}**\n`;
            res += `• **Pending Clarification**: **${pendingList.length > 0 ? pendingList.length : (studentDoubts.length - solvedList.length)}**\n`;
            res += `• **Solved / Clarified**: **${solvedList.length}**\n`;

            if (studentDoubts.length > 0) {
                const latestDoubt = studentDoubts[0];
                res += `\n📌 **Latest Doubt**: "${latestDoubt.title || latestDoubt.description || "Student Question"}" by ${latestDoubt.student_name || "Student"}`;
            }
            return res;
        }

        // 7. Student Counts & Enrolment
        if (q.includes("assigned to me") || q.includes("how many student") || q.includes("student count") || q.includes("total student") || q.includes("enrolled")) {
            return `👥 **Student Counts & Enrolment**:\n\n• **Total Enrolled Students**: **${totalStudents}**\n• **Present Today**: **${presentCount}**\n• **Absent Today**: **${absentCount}**\n• **Total Classes**: **${classOptions.length}**\n• **Total Sections**: **${allSectionOptions.length}**`;
        }

        // 8. Accessible Modules & Module Analysis
        if (q.includes("module") || q.includes("access") || q.includes("uses") || q.includes("guide")) {
            let res = `🚀 **My Accessible Modules & Uses Guide**:\n\n`;
            if (modules.length > 0) {
                modules.forEach((m) => {
                    res += `• **${m.label}**: ${m.desc || "Accessible dashboard module for lecturer operations."}\n`;
                });
            } else {
                res += `• **Attendance**: Track daily class & section student turnout.\n`;
                res += `• **Student Doubts**: View & clarify student questions in real-time.\n`;
                res += `• **Exam Reports**: Analyze exam rankings, highest marks & performance scorecards.\n`;
                res += `• **Schedule**: View weekly teaching periods & class allocations.\n`;
            }
            return res;
        }

        // 9. Overall Dashboard Statistics
        if (q.includes("overall") || q.includes("summary") || q.includes("statistic") || q.includes("stats")) {
            return `📊 **Overall Dashboard Statistics for ${lecturerName}**:\n
• **Total Enrolled Students**: **${totalStudents}** (${presentCount} Present, ${absentCount} Absent today)
• **Average Attendance**: **${averageAttendance}%**
• **Assigned Scope**: **${classOptions.length} Classes** & **${allSectionOptions.length} Sections**
• **Student Doubts**: **${studentDoubts.length}** total doubts
• **Exam Reports**: **${examReports.length}** completed exams available`;
        }

        // 10. Exam Reports / Highest Marks
        if (q.includes("exam") || q.includes("highest score") || q.includes("top exam") || q.includes("test")) {
            if (examReports.length === 0) {
                return `📝 No completed exam reports are currently loaded on the dashboard.`;
            }
            const topExam = examReports.reduce((prev, curr) => {
                const pVal = Number(prev.highPct || prev.highMarks || 0);
                const cVal = Number(curr.highPct || curr.highMarks || 0);
                return cVal > pVal ? curr : prev;
            }, examReports[0]);

            return `🏆 **Top Performing Exam**:\n\n• **Exam Name**: **${topExam.name}** (${topExam.className ? `Class ${topExam.className}` : ""})
• **Highest Score**: **${topExam.highPct}%**
• **Average Score**: **${topExam.avgPct}%**
• **Pass Rate**: **${topExam.passPct}%**
• **Appeared Students**: **${topExam.appeared} / ${topExam.totalStudents || "-"}**`;
        }

        // Fallback
        return "The requested information is not available in the current dashboard data.";
    };

    render() {
        const { isOpen, isMinimized, isMaximized, messages, inputMessage } = this.state;

        const suggestedQuestions = [
            "Class-wise & section-wise attendance details",
            "Show Subject Performance statistics",
            "Show Top Performing Students & Attention list",
            "Show Student Doubts summary (Pending vs Solved)",
            "What are my Accessible Modules and their uses?",
            "Show overall dashboard statistics",
        ];

        if (!isOpen) {
            return (
                <div
                    role="button"
                    onClick={this.toggleChat}
                    style={{
                        position: "fixed",
                        bottom: 24,
                        right: 24,
                        zIndex: 9999,
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                        boxShadow: "0 8px 24px rgba(124, 58, 237, 0.45)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                    title="Open AI Dashboard Assistant"
                >
                    <i className="fal fa-robot" style={{ fontSize: 26 }} />
                    <span
                        style={{
                            position: "absolute",
                            top: 2,
                            right: 2,
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            background: "#22c55e",
                            border: "2px solid #fff",
                        }}
                    />
                </div>
            );
        }

        const widthVal = isMaximized ? 580 : 400;
        const heightVal = isMinimized ? 52 : (isMaximized ? 640 : 520);

        return (
            <div
                style={{
                    position: "fixed",
                    bottom: 24,
                    right: 24,
                    zIndex: 9999,
                    width: widthVal,
                    height: heightVal,
                    maxWidth: "calc(100vw - 32px)",
                    maxHeight: "calc(100vh - 48px)",
                    borderRadius: 16,
                    boxShadow: "0 16px 40px rgba(15, 23, 42, 0.25)",
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    transition: "all 0.25s ease-in-out",
                }}
            >
                {/* ── Chatbot Header ── */}
                <div
                    style={{
                        background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
                        color: "#fff",
                        padding: "12px 16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                    }}
                    onClick={this.toggleMinimize}
                >
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                background: "rgba(255, 255, 255, 0.15)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <i className="fal fa-robot" style={{ color: "#38bdf8", fontSize: 18 }} />
                        </div>
                        <div>
                            <h6 className="mb-0 font-weight-bold text-white" style={{ fontSize: 14 }}>
                                AI Dashboard Assistant
                            </h6>
                            <span style={{ fontSize: 11, color: "#a5b4fc" }}>Real-time Analytics Assistant</span>
                        </div>
                    </div>

                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <button
                            type="button"
                            className="btn btn-sm text-white p-0"
                            style={{ width: 24, height: 24, fontSize: 14, opacity: 0.8 }}
                            onClick={this.toggleMinimize}
                            title={isMinimized ? "Expand" : "Minimize"}
                        >
                            <i className={isMinimized ? "fal fa-chevron-up" : "fal fa-minus"} />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm text-white p-0"
                            style={{ width: 24, height: 24, fontSize: 14, opacity: 0.8 }}
                            onClick={this.toggleMaximize}
                            title={isMaximized ? "Restore" : "Maximize"}
                        >
                            <i className={isMaximized ? "fal fa-compress-alt" : "fal fa-expand-alt"} />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm text-white p-0"
                            style={{ width: 24, height: 24, fontSize: 14, opacity: 0.8 }}
                            onClick={this.toggleChat}
                            title="Close"
                        >
                            <i className="fal fa-times" />
                        </button>
                    </div>
                </div>

                {/* ── Chatbot Body ── */}
                {!isMinimized && (
                    <>
                        <div
                            style={{
                                flex: 1,
                                padding: 14,
                                background: "#f8fafc",
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 12,
                            }}
                        >
                            {messages.map((msg, index) => (
                                <div
                                    key={index}
                                    style={{
                                        alignSelf: msg.sender === "user" ? "flex-end" : "flex-start",
                                        maxWidth: "88%",
                                    }}
                                >
                                    <div
                                        style={{
                                            background: msg.sender === "user" ? "linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)" : "#ffffff",
                                            color: msg.sender === "user" ? "#ffffff" : "#1e293b",
                                            padding: "10px 14px",
                                            borderRadius: msg.sender === "user" ? "16px 16px 2px 16px" : "16px 16px 16px 2px",
                                            boxShadow: msg.sender === "user" ? "0 4px 12px rgba(79, 70, 229, 0.2)" : "0 2px 8px rgba(0, 0, 0, 0.05)",
                                            border: msg.sender === "user" ? "none" : "1px solid #e2e8f0",
                                            fontSize: 13,
                                            whiteSpace: "pre-line",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {msg.text}
                                    </div>
                                    <span
                                        style={{
                                            fontSize: 10,
                                            color: "#94a3b8",
                                            display: "block",
                                            marginTop: 3,
                                            textAlign: msg.sender === "user" ? "right" : "left",
                                        }}
                                    >
                                        {msg.time}
                                    </span>
                                </div>
                            ))}

                            {/* ── Clickable Suggested Questions Chips ── */}
                            <div className="mt-2">
                                <span className="text-muted d-block mb-2" style={{ fontSize: 11, fontWeight: 700 }}>
                                    💡 Click to ask AI:
                                </span>
                                <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                                    {suggestedQuestions.map((qText, qIdx) => (
                                        <button
                                            key={qIdx}
                                            type="button"
                                            onClick={() => this.handleQuestionClick(qText)}
                                            style={{
                                                background: "#eff6ff",
                                                color: "#2563eb",
                                                border: "1px solid #bfdbfe",
                                                borderRadius: 16,
                                                padding: "4px 12px",
                                                fontSize: 11.5,
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                textAlign: "left",
                                                transition: "all 0.2s",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = "#dbeafe";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = "#eff6ff";
                                            }}
                                        >
                                            {qText}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div ref={this.chatEndRef} />
                        </div>

                        {/* ── Chatbot Footer Input ── */}
                        <Form onSubmit={this.handleSendMessage} style={{ padding: 10, background: "#fff", borderTop: "1px solid #e2e8f0" }}>
                            <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                <Form.Control
                                    type="text"
                                    placeholder="Ask AI about performance, doubts, classes..."
                                    value={inputMessage}
                                    onChange={(e) => this.setState({ inputMessage: e.target.value })}
                                    style={{
                                        borderRadius: 20,
                                        fontSize: 12.5,
                                        padding: "8px 14px",
                                        border: "1px solid #cbd5e1",
                                    }}
                                />
                                <Button
                                    type="submit"
                                    disabled={!inputMessage.trim()}
                                    style={{
                                        borderRadius: "50%",
                                        width: 36,
                                        height: 36,
                                        padding: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                                        border: "none",
                                        flexShrink: 0,
                                    }}
                                >
                                    <i className="fal fa-paper-plane text-white" style={{ fontSize: 13 }} />
                                </Button>
                            </div>
                        </Form>
                    </>
                )}
            </div>
        );
    }
}

export default DashboardAIChatbot;
